'use client';

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";

export default function Home() {
  const t = useTranslations();
  const pathname = usePathname();
  const currentLocale = pathname.match(/^\/([a-z]{2}-[A-Z]{2})/)?.[1] || 'zh-CN';
  const [settings, setSettings] = useState({
    siteName: "企业名称",
    siteDescription: "专业的企业服务提供商，致力于为客户提供高品质的产品和服务。",
    sitePhone: "400-123-4567",
    siteEmail: "info@example.com",
    siteAddress: "北京市朝阳区某某大厦",
    enterpriseIntroImage: "",
    enterpriseSolutionImage: ""
  });
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch('/api/settings');
        if (!response.ok) {
          throw new Error('Failed to fetch settings');
        }
        const data = await response.json();
        setSettings(data);
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };

    const fetchProducts = async () => {
      try {
        const response = await fetch('/api/products');
        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }
        const data = await response.json();
        setProducts(data);
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
    fetchProducts();
  }, []);

  return (
    <div>
      {/* 英雄区域 */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 mb-10 md:mb-0">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('home.hero.title')}</h1>
            <p className="text-xl mb-8">{t('home.hero.description')}</p>
            <div className="flex space-x-4">
              <Link href={`/${currentLocale}/products`} className="bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors">
                {t('home.hero.buttons.learnMore')}
              </Link>
              <Link href={`/${currentLocale}/contact`} className="border border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white hover:text-blue-600 transition-colors">
                {t('home.hero.buttons.contactUs')}
              </Link>
            </div>
          </div>
          <div className="md:w-1/2">
            <Image 
              src={settings.enterpriseSolutionImage || "/images/home/hero.svg"} 
              alt="企业解决方案" 
              width={600} 
              height={400} 
              className="rounded-lg shadow-xl"
            />
          </div>
        </div>
      </section>

      {/* 公司简介 */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">{t('home.about.title')}</h2>
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-10 md:mb-0 md:pr-10">
              <Image 
              src={settings.enterpriseIntroImage || "/images/home/about.svg"} 
              alt={t('home.about.teamImage')} 
              width={600} 
              height={400} 
              className="rounded-lg shadow-lg"
            />
            </div>
            <div className="md:w-1/2">
              <h3 className="text-2xl font-semibold mb-4">{t('home.about.mission.title')}</h3>
              <p className="text-black mb-6">
                {t('home.about.mission.description')}
              </p>
              <h3 className="text-2xl font-semibold mb-4">{t('home.about.advantages.title')}</h3>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3">✓</span>
                  <span>{t('home.about.advantages.items.technicalTeam')}</span>
                </li>
                <li className="flex items-center">
                  <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3">✓</span>
                  <span>{t('home.about.advantages.items.industryExperience')}</span>
                </li>
                <li className="flex items-center">
                  <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3">✓</span>
                  <span>{t('home.about.advantages.items.customSolutions')}</span>
                </li>
                <li className="flex items-center">
                  <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3">✓</span>
                  <span>{t('home.about.advantages.items.afterSalesService')}</span>
                </li>
              </ul>
              <Link href={`/${currentLocale}/about`} className="mt-8 inline-block text-blue-600 font-medium hover:underline">
                {t('home.about.learnMore')} →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 产品展示预览 */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">{t('home.products.title')}</h2>
          {loading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : products.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {products.map((product) => (
                <div key={product.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                  <Image 
                    src={product.image} 
                    alt={product.name} 
                    width={400} 
                    height={300} 
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-2">{product.name}</h3>
                    <p className="text-black mb-4">{product.description}</p>
                    <Link href={`/${currentLocale}/products`} className="text-blue-600 font-medium hover:underline">
                      {t('home.products.viewDetails')} →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-lg text-gray-600">暂无产品数据</p>
              <Link href={`/${currentLocale}/admin/products`} className="mt-4 inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors">
                前往添加产品
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* 客户评价 */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">{t('home.testimonials.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 flex">
                  ★★★★★
                </div>
              </div>
              <p className="text-black mb-6">
                {t('home.testimonials.items.item1.content')}
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gray-300 rounded-full mr-4"></div>
                <div>
                  <h4 className="font-semibold">{t('home.testimonials.items.item1.name')}</h4>
                  <p className="text-sm text-black">{t('home.testimonials.items.item1.position')}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 flex">
                  ★★★★★
                </div>
              </div>
              <p className="text-black mb-6">
                {t('home.testimonials.items.item2.content')}
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gray-300 rounded-full mr-4"></div>
                <div>
                  <h4 className="font-semibold">{t('home.testimonials.items.item2.name')}</h4>
                  <p className="text-sm text-black">{t('home.testimonials.items.item2.position')}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 flex">
                  ★★★★★
                </div>
              </div>
              <p className="text-black mb-6">
                {t('home.testimonials.items.item3.content')}
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gray-300 rounded-full mr-4"></div>
                <div>
                  <h4 className="font-semibold">{t('home.testimonials.items.item3.name')}</h4>
                  <p className="text-sm text-black">{t('home.testimonials.items.item3.position')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 联系我们 */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">{t('home.contact.title')}</h2>
          <div className="bg-white rounded-lg shadow-md p-8 max-w-3xl mx-auto">
            <p className="text-center text-black mb-8">
              {t('home.contact.description')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-blue-600 text-2xl">📞</span>
                </div>
                <h3 className="font-semibold mb-2">{t('home.contact.info.phone')}</h3>
                <p className="text-black">{settings.sitePhone}</p>
              </div>
              <div>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-blue-600 text-2xl">✉️</span>
                </div>
                <h3 className="font-semibold mb-2">{t('home.contact.info.email')}</h3>
                <p className="text-black">{settings.siteEmail}</p>
              </div>
              <div>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-blue-600 text-2xl">📍</span>
                </div>
                <h3 className="font-semibold mb-2">{t('home.contact.info.address')}</h3>
                <p className="text-black">{settings.siteAddress}</p>
              </div>
            </div>
            <div className="mt-10 text-center">
              <Link href={`/${currentLocale}/contact`} className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors">
                {t('home.contact.submitConsultation')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}